import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Grid, Text, Group, Ellipsis, Divider } from '@logz-ui/styleguide';
import { DateRange, hasAbsoluteInRange } from '@logz-pkg/utils';
import { DateRangePickerSubject } from '@logz-pkg/enums';
import { QuickOption } from '../types';
import { getQuickOptionSubject, isEqualDateRange } from './utils';
import { defaultQuickOptions } from './default-quick-options';
import { StyledTitle, StyledLink } from './QuickFilter.styles';
import { ActionsBar } from './ActionsBar';
import { ManualInputSection } from './ManualInputSection';
import { quickFilterUtils } from './quick-filter-utils';

interface IQuickFilterProps {
  onChange: (range: DateRange) => void;
  onConfirm: (range: DateRange) => void;
  onCancel: () => void;
  quickOptions?: QuickOption[];
  value?: DateRange;
  showActionsBar?: boolean;
  footerNotification?: React.ReactNode;
  actionsBarNotification?: React.ReactNode;
  updateSelectedRange: (range: DateRange) => void;
  lastRelativeValue: DateRange | null;
  timezone?: string;
}

export const QuickFilter: React.FC<IQuickFilterProps> = ({
  value,
  onChange,
  onConfirm,
  onCancel,
  quickOptions,
  showActionsBar,
  footerNotification,
  actionsBarNotification,
  updateSelectedRange,
  lastRelativeValue,
  timezone,
}) => {
  const options = quickOptions || defaultQuickOptions;
  const gridRowsNumber = Math.ceil(options.length / 2);

  const [selectedDate, setSelectedDate] = useState<(DateRange & { id?: string; skipConfirm?: boolean }) | null>(
    value ?? null,
  );
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] = useState(false);

  useEffect(() => {
    if (value && hasAbsoluteInRange(value) && lastRelativeValue && !isWaitingForConfirmation) {
      updateSelectedRange(lastRelativeValue);
    }
  }, [lastRelativeValue, value, updateSelectedRange, isWaitingForConfirmation]);

  useEffect(() => {
    if (isEqualDateRange(value ?? null, selectedDate)) return;

    setSelectedDate(value ?? null);
  }, [value?.start?.value, value?.end?.value]);

  const handleConfirm = useCallback(() => {
    const { start, end } = selectedDate ?? {};

    setIsWaitingForConfirmation(false);

    if (start && end) {
      onConfirm({ start, end });
    }
  }, [onConfirm, selectedDate]);

  const handleCancel = useCallback(() => {
    setIsWaitingForConfirmation(false);
    setSelectedDate(value ?? null);
    onCancel();
  }, [onCancel, value]);

  const handleQuickOptionClick = useCallback(
    ({ id, type, start, end, skipConfirm }) => {
      const payload: DateRange = {
        start: { type: type ?? 'relative', value: typeof start === 'function' ? start() : start },
        end: { type: type ?? 'relative', value: typeof end === 'function' ? end() : end },
      };

      onChange(payload);
      setIsWaitingForConfirmation(true);
      setSelectedDate({ id, skipConfirm, ...payload });
    },
    [onChange],
  );

  const handleManualInputChange = useCallback(
    (dateRange: DateRange) => {
      onChange(dateRange);
      setSelectedDate(dateRange);
    },
    [onChange],
  );

  const quickOptionsList = useMemo(() => {
    return options.map(({ id, label, type, start, end, skipConfirm, secondaryLabel }, key) => {
      const selected = quickFilterUtils.isQuickOptionSelected({ id, start, end, value, selectedDate });

      return (
        <Group key={key} gap={1}>
          <StyledLink
            selected={selected}
            size={12}
            weight={500}
            subject={getQuickOptionSubject({ id, label })}
            onClick={() => handleQuickOptionClick({ id, type, start, end, skipConfirm })}
          >
            {label}
          </StyledLink>
          {secondaryLabel && (
            <Ellipsis>
              <Text subject={DateRangePickerSubject.SecondaryLabel} color={'gray.600'} size={12}>
                {secondaryLabel}
              </Text>
            </Ellipsis>
          )}
        </Group>
      );
    });
  }, [options, value, selectedDate, handleQuickOptionClick]);

  return (
    <Group width={'100%'} vertical gap={0} px={4}>
      <Group width={'100%'} vertical py={2} gap={3}>
        <ManualInputSection value={selectedDate ?? value} onChange={handleManualInputChange} timezone={timezone} />
      </Group>
      <Divider color="gray.400" />
      <Group width={'100%'} vertical py={4} gap={3}>
        <StyledTitle weight={700} size={14}>
          Frequently used
        </StyledTitle>
        <Grid
          columns={2}
          rowGap={1}
          width={'100%'}
          gridAutoFlow={'column'}
          gridTemplateRows={`repeat(${gridRowsNumber}, 1fr)`}
        >
          {quickOptionsList}
        </Grid>

        {footerNotification}
      </Group>

      <ActionsBar
        disabled={!selectedDate}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        notification={actionsBarNotification}
      />
    </Group>
  );
};
